import get from "lodash/get";
import trimEnd from "lodash/trimEnd";
import { object, string } from "prop-types";
import React from "react";
import { useSelector } from "react-redux";

import { Link } from "@quintype/components";

import "./breadcrumb.m.css";

const BreadCrumb = ({ story, type }) => {
  const qt = useSelector((state) => get(state, ["qt"]));
  const author = get(qt, ["data", "author"], {});
  const section = get(story, ["slug"], "").split("/");
  const allStorySections = get(story, ["sections"], []);
  const allSections = get(qt, ["config", "sections"], []);
  const currentSection = get(qt, ["data", "section"], {});
  const staticPageTitle = get(qt, ["data", "title"], "");
  const top15RankingsTitle = transformString(get(qt, ["data", "slug"], ""));
  const tagPageData = get(qt, ["data", "tag"], {});

  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const isManageSubscription = pathname === "/profile/subscription";

  let breadCrumbs = [];

  const getSectionObject = (currentSlug) => allStorySections.find((a) => a.slug === currentSlug);
  function buildBreadCrumbs(sectionSlugs, breadCrumbs = []) {
    if (sectionSlugs.length === 0) return breadCrumbs;

    const [currentSlug, ...remainingSlugs] = sectionSlugs;
    const currentSection = getSectionObject(currentSlug);

    if (currentSection) {
      breadCrumbs.push(currentSection);
      return buildBreadCrumbs(remainingSlugs, breadCrumbs);
    } else if (type === "story-page") {
      breadCrumbs.push(story.headline);
    }
    return breadCrumbs;
  }
  function transformString(str) {
    return str
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  if (type === "story-page" || type === "custom-event-page") {
    let currentSlug = "";
    section.forEach((crumb, index) => {
      if (index < section.length - 2) {
        currentSlug = currentSlug.concat(crumb, "/");
        breadCrumbs.push({ name: transformString(crumb), slug: trimEnd(currentSlug, "/") });
      } else if (index === section.length - 1) {
        breadCrumbs.push({});
      }
    });
  } else {
    breadCrumbs = buildBreadCrumbs(section);
  }

  if (type === "section-page") {
    const parentSectionId = get(qt, ["data", "section", "parent-id"], "");
    let parentSection = {};
    parentSectionId && (parentSection = allSections?.find((section) => section?.id === parentSectionId));
    parentSection?.name && breadCrumbs.push(parentSection);
    breadCrumbs.push(currentSection);
  }

  let content;
  switch (type) {
    case "story-page":
      content = story?.headline;
      break;
    case "author-page":
      const authors = { name: "Author", slug: "author" };
      breadCrumbs.push(authors, author);
      content = author?.name;
      break;
    case "section-page":
      content = currentSection?.name;
      break;
    case "authors-list":
      breadCrumbs.push({});
      content = "Author";
      break;
    case "ytshorts-page":
      breadCrumbs.push({});
      content = "Short Videos";
      break;
    case "static-page":
      breadCrumbs.push({});
      content = staticPageTitle;
      break;
    case "all-rankings":
      breadCrumbs.push({});
      content = "Rankings";
      break;
    case "top15-rankings":
      breadCrumbs.push({ name: "Rankings", slug: "rankings" }, {});
      content = top15RankingsTitle;
      break;
    case "tag-page":
      breadCrumbs.push({ name: "Topic" }, { name: tagPageData?.name });
      content = tagPageData?.name;
      break;
    case "all-companies":
      breadCrumbs.push({});
      content = "Companies";
      break;
    case "people-ranking":
      breadCrumbs.push({});
      content = "People";
      break;
    case "magazines-issue-landing-page":
      breadCrumbs.push({ name: "Magazine", slug: "magazine" }, { name: "" });
      content = qt?.data?.magazineIssue?.data?.slug || "";
      break;
    case "all-magazines-listing-page":
      breadCrumbs.push({});
      content = "Magazine";
      break;
    case "company-page":
      breadCrumbs.push({ name: "Companies", slug: "companies" }, { name: "" });
      content = qt?.data?.companyInfo?.name || "";
      break;
    case "individual-page":
      breadCrumbs.push({ name: "People", slug: "people" }, { name: "" });
      content = qt?.data?.individualInfo?.["display-name"] || qt?.data?.individualInfo?.name || "";
      break;
    case "custom-event-page":
      content = story?.headline;
      break;
    case "mnc-500-detail":
      const mncType = qt.currentPath.split("/")[2];
      breadCrumbs.push(
        { name: "Rankings", slug: "rankings" },
        { name: transformString(mncType), slug: `rankings/${mncType}` },
        { name: "" }
      );
      content = qt.currentPath.split("/")[3].split("?")[0];
      break;
    case "fortune-500":
    case "the-next-500":
    case "the-emerging-companies":
    case "best-b-schools-detail":
    case "most-powerful-women":
    case "40-under-40":
    case "indias-top-billionaires":
    case "indias-top-billionaires/2025":
    case "indias-most-valuable-celebrities":
    case "indias-most-valuable-celebrities/2026":
      const type = qt.currentPath.split("/")[2];
      breadCrumbs.push(
        { name: "Rankings", slug: "rankings" },
        { name: transformString(type), slug: `rankings/${type}` },
        { name: "" }
      );
      content = qt.currentPath.split("/")[3].split("?")[0];
      break;
    case "search-page":
      breadCrumbs.push({});
      content = `Search`;
      break;
    case "subscription-page":
      breadCrumbs.push({});
      content = "Subscription";
      break;
    case "profile-page":
      breadCrumbs.push({});
      content = `Profile${isManageSubscription ? " / Subscription" : ""}`;
      break;
    case "collection-page":
      breadCrumbs.push({ name: "Collection", slug: "collection" }, { name: "" });
      content = transformString(qt.currentPath?.split("/")[2]?.split("?")[0] ?? "");
      break;
    default:
      content = "";
  }

  return (
    <div styleName="breadcrumb-wrapper" className="breadcrumb-wrapper">
      <div styleName="breadcrumb" className="breadcrumb">
        <Link href="/" disableAjaxLinks={true}>
          <span styleName="brand-color" className="brand-color">
            Home
          </span>
        </Link>
        {breadCrumbs.map((crumb, index) => {
          if (index === 0 && (type === "tag-page" || type === "collection-page")) {
            return (
              <span key={index} styleName="headline-text" className="breadcrumb-headline-text">
                {crumb.name}
              </span>
            );
          }

          if (index === breadCrumbs.length - 1) {
            return (
              <span key={index} styleName="headline-text" className="breadcrumb-headline-text ellipsis">
                {content}
              </span>
            );
          }

          return (
            <Link key={index} href={`/${crumb.slug}`} disableAjaxLinks={true}>
              <span styleName="brand-color" key={index} className="brand-color">
                {crumb.name}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

BreadCrumb.propTypes = {
  story: object,
  type: string,
};

export default BreadCrumb;
